import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { BuyerProfile, buyerProfileSchema, Listing, SchemaKey, User } from 'shared/@types';
import { KeyValueErrors } from 'shared/library/use-api';
import { ContactAccountField, ContactDraft } from 'shared/library/contact-draft';
import { createMessageClientId } from 'shared/components/chat/message-utils';
import type { ContactTarget } from 'shared/library/contact-target';

export interface ConversationWizardSupport {
  target: ContactTarget | null;
  listing: Listing | null;
  latestMessage: string | null;
  viewedIntro: boolean;
  viewedImage: boolean;
  email: string;
  firstName: string;
  lastName: string;
  personalMessage: string;
  conversationUuid: string | null;
  answeredQuestions: SchemaKey[];
  needsAccountDetails: boolean;
  accountDetailFields: ContactAccountField[];
  pendingAccountFields: ContactAccountField[];
  resumingAfterLogin: boolean;
  existingConversationUuid: string | null;
  messageClientId: string;
  pendingCreateMessage: string | null;
  pendingCreateClientId: string | null;
}

const defaultSupport: ConversationWizardSupport = {
  target: null,
  listing: null,
  latestMessage: null,
  viewedIntro: false,
  viewedImage: false,
  email: '',
  firstName: '',
  lastName: '',
  personalMessage: '',
  conversationUuid: null,
  answeredQuestions: [],
  needsAccountDetails: false,
  accountDetailFields: [],
  pendingAccountFields: [],
  resumingAfterLogin: false,
  existingConversationUuid: null,
  messageClientId: '',
  pendingCreateMessage: null,
  pendingCreateClientId: null,
};

const useConversationWizardStore = create(
  combine(
    {
      data: null as BuyerProfile | null,
      support: defaultSupport,
      errors: {} as KeyValueErrors,
    },
    (set) => ({
      setup: (
        user: User,
        listingOrTarget: Listing | ContactTarget,
        latestMessage: string | null,
        draft?: ContactDraft | null,
        existingConversationUuid: string | null = null,
        resumingAfterLogin = false,
      ) => {
        const target: ContactTarget =
          'kind' in listingOrTarget
            ? listingOrTarget
            : { kind: 'listing', listing: listingOrTarget };
        const pendingAccountFields = (['first_name', 'last_name', 'email'] as const).filter(
          (field) => draft?.pendingAccountFields?.includes(field),
        );
        const accountDetailFields = (['first_name', 'last_name', 'email'] as const).filter(
          (field) => !user[field]?.trim() || pendingAccountFields.includes(field),
        );
        set({
          data: user.buyer_profile ?? {},
          errors: {},
          support: {
            target,
            listing: target.kind === 'listing' ? target.listing : null,
            latestMessage,
            viewedIntro: user.buyer_profile !== null,
            viewedImage: user.buyer_profile !== null || user.avatar !== null,
            email:
              pendingAccountFields.includes('email') || !user.email?.trim()
                ? (draft?.email ?? '')
                : user.email,
            firstName:
              pendingAccountFields.includes('first_name') || !user.first_name?.trim()
                ? (draft?.firstName ?? '')
                : user.first_name,
            lastName:
              pendingAccountFields.includes('last_name') || !user.last_name?.trim()
                ? (draft?.lastName ?? '')
                : user.last_name,
            personalMessage: draft?.message ?? '',
            conversationUuid: null,
            answeredQuestions: (Object.keys(buyerProfileSchema) as SchemaKey[]).filter(
              (key) =>
                existingConversationUuid ||
                (buyerProfileSchema[key] as readonly string[]).includes(
                  user.buyer_profile?.[key] ?? '',
                ),
            ),
            needsAccountDetails:
              pendingAccountFields.length > 0 ||
              (!existingConversationUuid && accountDetailFields.length > 0),
            accountDetailFields,
            pendingAccountFields,
            resumingAfterLogin,
            existingConversationUuid,
            messageClientId: draft?.messageClientId ?? createMessageClientId(),
            pendingCreateMessage: draft?.pendingCreateMessage ?? null,
            pendingCreateClientId: draft?.pendingCreateClientId ?? null,
          },
        });
      },
      reset: () =>
        set({
          data: null,
          support: defaultSupport,
          errors: {},
        }),
      setData: (newData: Partial<BuyerProfile>) => {
        set((state) => ({ data: { ...state.data!, ...newData } }));
      },
      setSupport: (newData: Partial<ConversationWizardSupport>) => {
        set((state) => ({ support: { ...state.support, ...newData } }));
      },
    }),
  ),
);

export default useConversationWizardStore;
